// main.ts
// Runtime Configuration Loading Process:
// 1. SHINE_AI_FRONTEND_URL is compiled in at build time via webpack.DefinePlugin
// 2. SHINE_AI_WEB_URL and SHINE_AI_API_URL are loaded from config.js at runtime
// 3. initializeChat() calls ensureConfigLoaded() which ensures config.js is loaded before proceeding
// 4. environment.ts provides a uniform way to access all three variables
// 
// This approach ensures that:
// - The frontend URL is always available (compiled in)
// - Web and API URLs are loaded from config.js at runtime
// - Nothing happens until config.js is loaded and variables are set
import { environment, ensureConfigLoaded } from '../config/environment';
import { hydrateDynamicWidgetText } from './loading/dynamicWidgetText';
import { fetchPopupConfig } from './loading/configFetcher';
import { buildDynamicWidgetPromptRequest } from './loading/pageContextExtractor';
import { PopupComponent } from './loading/PopupComponent';
import { registerPreviewOpenContract } from './loading/previewOpen';
import { ensureMaterialSymbols } from './utils/icons';
import { withScopedWidgetSession } from './utils/widgetSessionCache';
const attachPreviewPopup = registerPreviewOpenContract(window);
function loadMaterialSymbols() { ensureMaterialSymbols(); }
async function loadGlobalStyles(kind, useShadowDom) {
    if (useShadowDom) {
        return;
    }
    if (kind === 'widget') {
        await import('./widget/styles.scss');
        return;
    }
    await import('./inline/styles.scss');
}
function waitForWidgetModal(timeoutMs = 2000) {
    return new Promise(resolve => {
        const start = Date.now();
        const check = () => {
            const widget = document.querySelector('shine-widget-component');
            const root = widget?.getRenderRoot?.() ?? widget?.shadowRoot ?? document;
            if (root.querySelector('#shineModal') || Date.now() - start >= timeoutMs) {
                resolve();
                return;
            }
            window.setTimeout(check, 50);
        };
        check();
    });
}
async function ensureWidgetRuntime(useShadowDom) {
    const [{ WidgetChatComponent }, { CarCardComponent }, { ProductComponent }, { ProductNoPriceComponent },] = await Promise.all([
        import('./widget/WidgetChatComponent'),
        import('./product-card/car/CarCardComponent'),
        import('./product-card/product/ProductComponent'),
        import('./product-card/productNoPrice/ProductNoPriceComponent'),
    ]);
    if (!customElements.get('shine-widget-component')) {
        customElements.define('shine-widget-component', WidgetChatComponent);
    }
    if (!customElements.get('car-product-card')) {
        customElements.define('car-product-card', CarCardComponent);
    }
    if (!customElements.get('product-card')) {
        customElements.define('product-card', ProductComponent);
    }
    if (!customElements.get('product-no-price-card')) {
        customElements.define('product-no-price-card', ProductNoPriceComponent);
    }
    if (!document.querySelector('shine-widget-component')) {
        await loadGlobalStyles('widget', useShadowDom);
        document.body.appendChild(new WidgetChatComponent(window.shineSettings.client, window.shineSettings.chatbot, {
            shadowDom: useShadowDom,
        }));
    }
    await waitForWidgetModal();
}
// The loadRuntimeConfig function has been replaced by ensureConfigLoaded from environment.ts
// This ensures that config.js is loaded before any environment variables are accessed
// Function to get query parameters from URL
function getQueryParam(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
}
// Fallback: read query parameters from the widget script src (supports embed patterns like widget.js?client=...&chatbot=...)
function getScriptQueryParam(name) {
    try {
        const candidates = [];
        const current = document.currentScript;
        if (current)
            candidates.push(current);
        candidates.push(...Array.from(document.getElementsByTagName('script')));
        for (const script of candidates) {
            if (!script?.src)
                continue;
            try {
                const url = new URL(script.src, window.location.href);
                const value = url.searchParams.get(name);
                if (value)
                    return value;
            }
            catch {
                /* ignore malformed script src */
            }
        }
    }
    catch {
        /* ignore */
    }
    return null;
}
async function initializeChat() {
    loadMaterialSymbols();
    // Ensure config is loaded before proceeding
    // This will load config.js if needed and wait for it to complete
    await ensureConfigLoaded();
    // Preconnect to our API and Web (SignalR) backends to warm up TLS/HTTP2
    try {
        const ensurePreconnect = (href) => {
            if (!href)
                return;
            if (document.querySelector(`link[rel="preconnect"][href="${href}"]`))
                return;
            const l = document.createElement('link');
            l.rel = 'preconnect';
            l.href = href;
            l.crossOrigin = 'anonymous';
            document.head.appendChild(l);
        };
        ensurePreconnect(environment.SHINE_AI_API_URL);
        ensurePreconnect(environment.SHINE_AI_WEB_URL);
    }
    catch {
        /* preconnect hints are optional */
    }
    // Ensure window.shineSettings exists with sane defaults
    if (!window.shineSettings) {
        window.shineSettings = { client: "", chatbot: "", inlineOnly: false };
        console.warn("window.shineSettings was not defined. Using defaults; pass ?client=&chatbot= or set window.shineSettings before loading the widget.");
    }
    // Check for client and chatbot IDs in query parameters (page URL first, then script src)
    const clientIdFromQuery = getQueryParam('client') || getScriptQueryParam('client');
    const chatbotIdFromQuery = getQueryParam('chatbot') || getScriptQueryParam('chatbot');
    const debugFromQuery = getQueryParam('debug') || getScriptQueryParam('debug');
    const streamDebugScenarioFromQuery = getQueryParam('streamDebugScenario') || getScriptQueryParam('streamDebugScenario');
    // Override window.shineSettings with query parameters if they exist
    if (clientIdFromQuery) {
        window.shineSettings.client = clientIdFromQuery;
    }
    if (chatbotIdFromQuery) {
        window.shineSettings.chatbot = chatbotIdFromQuery;
    }
    if (typeof window.shineSettings.debug === 'undefined') {
        // Only set from query if not already configured programmatically
        window.shineSettings.debug = (debugFromQuery === '1' || debugFromQuery === 'true');
    }
    if (streamDebugScenarioFromQuery) {
        window.shineSettings.streamDebugScenario = streamDebugScenarioFromQuery;
        window.shineSettings.debug = true;
    }
    try {
        const useShadowDom = window.__shineUseShadowDom === true || window.shineSettings.shadowDom === true;
        const inlineLauncherElements = Array.from(document.querySelectorAll('shine-chat-launcher'));
        const hasFloatingLauncher = window.shineSettings.inlineOnly !== true;
        const hasAnyLauncher = hasFloatingLauncher || inlineLauncherElements.length > 0;
        if (hasAnyLauncher) {
            if (window.shineSettings.streamDebugScenario) {
                window.shineConfigs = {
                    colors: {
                        backgroundColor: '',
                        textColor: '',
                        closeButtonBackground: '',
                        closeButtonColor: '',
                    },
                    images: {
                        aiLogo1: '',
                        aiLogo2: '',
                        logoSwapEnabled: false,
                    },
                    text: {
                        aiMessage: 'Local stream debug',
                    },
                    widgetSession: 'local-stream-debug',
                    widgetSessionExpiresAtUtc: '2099-01-01T00:00:00.000Z',
                    widgetSessionClient: window.shineSettings.client,
                    widgetSessionChatbot: window.shineSettings.chatbot,
                };
                if (hasFloatingLauncher) {
                    await ensureWidgetRuntime(useShadowDom);
                    const widget = document.querySelector('shine-widget-component');
                    const root = widget?.getRenderRoot?.() ?? widget?.shadowRoot ?? document;
                    const modal = root.querySelector('#shineModal');
                    if (modal) {
                        modal.style.display = 'flex';
                    }
                }
            }
            else {
                const endpoint = environment.SHINE_AI_WEB_URL; // Using runtime configuration
                const promptRequest = buildDynamicWidgetPromptRequest(window.location.href);
                const widgetConfig = await fetchPopupConfig(endpoint, promptRequest);
                window.shineConfigs = withScopedWidgetSession(widgetConfig, window.shineSettings.client, window.shineSettings.chatbot);
                let previewPopup = null;
                if (hasFloatingLauncher) {
                    const popup = new PopupComponent(widgetConfig, {
                        ensureWidgetRuntime: () => ensureWidgetRuntime(useShadowDom),
                    });
                    previewPopup = popup;
                    void hydrateDynamicWidgetText(endpoint, widgetConfig, popup, promptRequest);
                }
                inlineLauncherElements.forEach(element => {
                    const popup = new PopupComponent(widgetConfig, {
                        ensureWidgetRuntime: () => ensureWidgetRuntime(useShadowDom),
                        mountTarget: element,
                        inlineLauncher: true,
                        launcherLabel: element.getAttribute('aria-label') || undefined,
                    });
                    previewPopup ??= popup;
                });
                if (previewPopup) {
                    attachPreviewPopup(previewPopup);
                }
            }
        }
        // Initialize inline chat components by replacing <shine-inline-chat> tags.
        // Each inline component uses the chatbot attribute value or falls back to widgetConfig.chatbotId.
        const inlineElements = document.querySelectorAll('shine-inline-chat');
        if (inlineElements.length > 0) {
            const { InlineChatComponent } = await import('./inline/InlineChatComponent');
            if (!customElements.get('shine-inline-component')) {
                customElements.define('shine-inline-component', InlineChatComponent);
            }
            await loadGlobalStyles('inline', useShadowDom);
            inlineElements.forEach(el => {
                const chatbotId = el.getAttribute('chatbot') || window.shineSettings.chatbot;
                const client = el.getAttribute('client') || window.shineSettings.client;
                el.replaceWith(new InlineChatComponent(client, chatbotId, {
                    shadowDom: useShadowDom,
                }));
            });
        }
    }
    catch (error) {
        console.error('Failed to initialise chat widgets', error);
    }
}
// Initialize chat when the page loads
// Configuration is now embedded in the compiled bundle
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initializeChat();
    });
}
else {
    initializeChat();
}
